import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { RoomEntity } from "@common/domain";
import {
  Actions,
  concatLatestFrom,
  createEffect,
  EffectNotification,
  ofType,
  OnRunEffects
} from "@ngrx/effects";
import { getSelectors, RouterReducerState } from "@ngrx/router-store";
import { createFeatureSelector, Store } from "@ngrx/store";
import {
  LocalConferenceStreamActions,
  RemoteConferenceStreamActions
} from "@youm-front/conference-stream/data-access";
import { YoumAPIFacade } from "@youm-front/core";
import { RoomError, RoomJoinCmd } from "@youm-front/room/domain";
import { ROOM_APP_URL } from "@youm-front/room/provider";
import { ErrorHandlerService } from "@youm-front/utils/error-handler";
import { Observable } from "rxjs";
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap
} from "rxjs/operators";
import { RoomActions, RoomMixerStreamActions, RoomV2Actions } from "../actions";
import { RoomContextFacade } from "../facades";
import { RoomService } from "../services";

const selectRouter = createFeatureSelector<RouterReducerState>("router");
const { selectRouteParams } = getSelectors(selectRouter);

@Injectable()
export class RoomV2Effects implements OnRunEffects {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private readonly errorHandlerService: ErrorHandlerService,
    @Inject(ROOM_APP_URL) readonly roomAppUrl: string,
    private readonly actions$: Actions,
    private readonly roomService: RoomService,
    private readonly roomContextFacade: RoomContextFacade,
    private readonly youmAPIFacade: YoumAPIFacade,
    private readonly store: Store
  ) {}

  init$ = createEffect(() =>
    this.youmAPIFacade.connected$.pipe(
      filter((_) => _),
      concatLatestFrom(() => [this.store.select(selectRouteParams)]),
      mergeMap(([, routeParams]) => [
        RoomActions.reset(),
        RoomMixerStreamActions.reset(),
        RoomActions.joinRoom({
          roomId: routeParams["roomId"],
          roomContextId: this.roomContextFacade.roomContextId
        }),
        RoomActions.loadRoomData({ roomId: routeParams["roomId"] })
      ])
    )
  );

  disposeRoomData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomActions.init, RoomActions.destroy, RoomActions.reset),
      mergeMap(() => [
        RoomActions.disposeRoomLocalData(),
        RoomActions.disposeRoomRemoteData()
      ])
    )
  );

  disposeRoomLocalData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomActions.disposeRoomLocalData),
      map(() => LocalConferenceStreamActions.dispose())
    )
  );

  disposeRoomRemoteData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomActions.disposeRoomRemoteData),
      map(() => RemoteConferenceStreamActions.dispose())
    )
  );

  roomGet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomActions.loadRoomData),
      switchMap((_) =>
        this.roomService.roomGet(_.roomId).pipe(
          switchMap((_: RoomEntity) => [
            RoomActions.loadRoomDataSuccess({ room: _ })
          ]),
          catchError((_: Error) => [
            RoomActions.loadRoomDataFailure({ error: _.message })
          ])
        )
      )
    )
  );

  roomJoin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomActions.joinRoom),
      switchMap((_) =>
        this.roomService
          .roomJoin(
            new RoomJoinCmd({
              roomContextId: _.roomContextId,
              roomId: _.roomId
            })
          )
          .pipe(
            mergeMap(() => [RoomActions.joinRoomSuccess()]),
            catchError((_) => [RoomActions.joinRoomFailure({ error: _ })])
          )
      )
    )
  );

  roomJoinFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RoomActions.joinRoomFailure),
        tap((_) => {
          if (_.error.code === RoomError.NOT_FOUND) {
            this.document.location.href = this.roomAppUrl;
            return;
          }

          this.errorHandlerService.error(_);
        })
      ),
    { dispatch: false }
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return this.actions$.pipe(
      ofType(RoomV2Actions.init),
      exhaustMap(() =>
        resolvedEffects$.pipe(
          takeUntil(this.actions$.pipe(ofType(RoomV2Actions.destroy)))
        )
      )
    );
  }
}
